import { Component } from 'react';
import { connect } from 'react-redux';
import lodash from 'lodash';

import styles from './DescriptorEditor.scss';
import icons from 'styles/icons/icons.scss';
import DataType from 'complionAdmin/components/BinderTemplateQuery/DataType/DataType';
import DescriptorEntry from 'indexDocuments/components/DescriptorEntry/DescriptorEntry';

import { descriptorShape } from 'indexDocuments/util/descriptorPropType';
import {
    changeEditorValue,
    addValue,
    removeValue,
    markValueInvalid,
    setHighlightIndex,
    setMessage,
    setValueEditMode,
    setValue,
    commitValueChange,
    markChangeValueInvalid,
} from 'indexDocuments/actions/descriptor';

class DescriptorEditor extends Component {
    constructor(props) {
        super(props);
        this.state = {
            errorOverride: false,
        };
    }

    render() {
        const {
            descriptor: {
                id,
                displayName,
                isRequiredForDocumentType: isRequired,
                isActiveForDocumentType: isActive,
                dataType,
                dataSourceId,
                dataset
            },
            values,
            touched,
            singleValueDescriptorValues,
            setupValues,
            editorValue,
            message,
            changeEditorValue,
            removeValue,
            highlightedIndex,
            setValue,
            setValueEditMode,
            markValueInvalid,
            commitValueChange,
            markChangeValueInvalid,
            dataSourceItems,
            allowedDataSourceValues,
        } = this.props;

        
        const allowedValuesExist = allowedDataSourceValues[id] && allowedDataSourceValues[id].allowedValues;
        const normalEntry = !allowedValuesExist || allowedDataSourceValues[id].allowedValues === null;
        const itemCount = allowedValuesExist ? allowedDataSourceValues[id].allowedValues.length : 0;
        
        let error = null;
        let errorText = [];
        this.state.errorOverride = false;

        if (isRequired && values.length === 0) {
            errorText.push('At least one value is required.');
        }
        if (!normalEntry && itemCount === 0) {
            errorText.push('No Values Have Been Set up in the Study. Please Contact Study Administrator.');
            // if there are no allowed values, make sure extra boxes don't appear.
            this.state.errorOverride = true;
        }
        if (errorText.length > 0) {
            error = errorText.join('\n');
        }

        return isActive ? (
            <div data-cy="DescriptorEditor"
                className={styles.descriptorEditor}
                onBlurCapture={this.handleBlurCapture.bind(this)}
                onKeyPress={this.handleKeyPress.bind(this)}
                onKeyDown={this.handleKeyDown.bind(this)}>
                <DescriptorLabel displayName={displayName}
                    touched={touched}
                    error={error}
                    errorOverride={this.state.errorOverride}
                    message={message}
                    isRequired={isRequired} />
                <DescriptorValueList descriptorId={id}
                    values={this.state.errorOverride ? [] : values}
                    dataType={dataType}
                    dataSourceId={dataSourceId}
                    dataset={dataset}
                    highlightedIndex={highlightedIndex}
                    setValue={setValue}
                    setValueEditMode={setValueEditMode}
                    commitValueChange={commitValueChange}
                    markValueInvalid={markChangeValueInvalid}
                    removeValue={removeValue}
                    editValueAtIndex={this.editValueAtIndex.bind(this)}
                    setupValues={setupValues}
                    singleValueDescriptorValues={singleValueDescriptorValues}/>
                <DescriptorEntry editorValue={editorValue}
                    onFocus={this.handleEntryFocus.bind(this, dataSourceItems)}
                    onBlur={this.handleEntryBlur.bind(this)}
                    onKeyDown={this.handleDescriptorEntryKeyDown.bind(this)}
                    changeEditorValue={changeEditorValue.bind(null, id)}
                    markValueInvalid={markValueInvalid.bind(null, id)}
                    dataType={dataType}
                    selectedValues={values}
                    dataSourceId={dataSourceId}
                    dataset={dataset}
                    setupValues={setupValues}
                    singleValueDescriptorValues={singleValueDescriptorValues}/>
            </div>
        ) : null;
    }

    addValue() {
        const {
            id,
            editorValue,
            addValue,
            invalidValue
        } = this.props;
        if (invalidValue) {
            return;
        }
        addValue(id, editorValue);
    }

    editValueAtIndex(index) {
        const {
            id,
            setValueEditMode,
            setHighlightIndex
        } = this.props;

        setHighlightIndex(id, index);
        setValueEditMode(id, index, true);
    }

    editHighlightedValue() {
        const { id, highlightedIndex, setValueEditMode, values } = this.props;
        if (highlightedIndex === -1) {
            return;
        }
        const value = values[highlightedIndex];
        if (!value) return;
        setValueEditMode(id, highlightedIndex, !value.editMode);
    }

    removeHighlightedValue() {
        const { id, highlightedIndex, removeValue } = this.props;
        if (highlightedIndex === -1) {
            return;
        }
        removeValue(id, highlightedIndex);
    }

    changeHighlightByOffset(offset) {
        const { id, highlightedIndex, setHighlightIndex } = this.props;
        setHighlightIndex(id, highlightedIndex + offset);
    }

    handleKeyPress(e) {
        const { highlightedIndex, editorValue } = this.props;
        switch(e.key) {
            case 'Enter':
                if (highlightedIndex === -1) {
                    if (!lodash.isString(editorValue) || !lodash.isEmpty(editorValue)) {
                        e.stopPropagation();
                        this.addValue();
                    }
                } else {
                    this.editHighlightedValue();
                    e.stopPropagation();
                }
                break;
        }
    }

    handleKeyDown(e) {
        const { highlightedIndex } = this.props;
        switch(e.key) {
            case 'ArrowUp':
                e.stopPropagation();
                this.changeHighlightByOffset(-1);
                break;
            case 'ArrowDown':
                e.stopPropagation();
                this.changeHighlightByOffset(1);
                break;
            case 'Delete':
                if (highlightedIndex !== -1) {
                    e.stopPropagation();
                    e.preventDefault();
                    this.removeHighlightedValue();
                }
                break;
        }
    }

    getAllowedValues(dataSourceId, dataset, setupValues, dataSourceItems) {
        let allowedValues = null;
        if (dataSourceId && setupValues) {
            allowedValues = lodash.filter(dataSourceItems[dataSourceId], (item) => {
                return lodash.includes(setupValues, item.value);
            });
        } else if (dataSourceId) {
            // if no setup values, just return all the items.
            allowedValues = dataSourceItems[dataSourceId];
        } else if (dataset) {
            allowedValues = dataset;
        }
        // if this is null, then this is a normal descriptor.
        return allowedValues;
    }

    // this is used to prevent the key presses from the datetime picker from
    // interfering with the selection mechanism here
    handleDescriptorEntryKeyDown(e) {
        const { id, setHighlightIndex } = this.props;
        if (e.target.getAttribute('aria-expanded') === 'true' ||
            (e.altKey && ['ArrowDown', 'ArrowUp'].includes(e.key))) {
            setHighlightIndex(id, -1);
            e.stopPropagation();
        }
    }

    handleBlurCapture(e) {
        const { id, setHighlightIndex } = this.props;
        setHighlightIndex(id, -1);
    }

    handleEntryFocus(e) {
        const { id, setMessage, values } = this.props;

        if (values.length === 0) {
            setMessage(id, 'Enter in a value below. Then press ENTER.');
        } else {
            setMessage(id, 'Select a value using UP and DOWN. Then press ENTER to edit, or DELETE to remove.');
        }
    }

    handleEntryBlur(e) {
        const { id, setMessage } = this.props;
        setMessage(id, null);
        this.addValue();
    }
}

DescriptorEditor.propTypes = {
    ...descriptorShape
};

const DescriptorLabel = ({ displayName, isRequired, touched, errorOverride, error, message }) => {
    const labelClass = error && touched
        ? styles.descriptorLabelError
        : styles.descriptorLabel;
    let helpTextClass;
    let requiredText;
    if ((isRequired && touched) || errorOverride) {
        helpTextClass = error ? styles.errorLabel : styles.requiredLabel;
        requiredText = error || 'Required';
    } else if (isRequired && !touched) {
        helpTextClass = styles.requiredLabel;
        requiredText = 'Required';
        styles.requiredLabel;
    } else {
        requiredText = '';
    }

    return (
        <div className={styles.labelContainer} data-cy="DescriptorEditor-DescriptorLabel">
            <div className={labelClass}>
                <span className={styles.displayNameLabel} data-cy="DescriptorEditor-span">
                    { displayName } { error && touched && '!' }
                </span>
                <span className={helpTextClass} data-cy="DescriptorEditor-requiredText">
                    { requiredText }
                </span>
            </div>
            <div className={styles.labelMessage} data-cy="DescriptorEditor-message">
                {message}
            </div>
        </div>
    );
};

const DescriptorValueList = ({
    descriptorId,
    values,
    highlightedIndex,
    dataType,
    dataSourceId,
    dataset,
    removeValue,
    setupValues,
    singleValueDescriptorValues,
    setValue,
    setValueEditMode,
    commitValueChange,
    markValueInvalid,
    editValueAtIndex
}) => {
    const renderedValues = lodash.map(values,
        ({value, editorValue, editMode}, index) =>
            <DescriptorValue key={index}
                descriptorId={descriptorId}
                value={value}
                editorValue={editorValue}
                index={index}
                dataType={dataType}
                dataSourceId={dataSourceId}
                dataset={dataset}
                highlighted={highlightedIndex === index}
                editMode={editMode}
                removeValue={removeValue}
                setValue={setValue}
                setValueEditMode={setValueEditMode}
                commitValueChange={commitValueChange}
                markValueInvalid={markValueInvalid}
                editValueAtIndex={editValueAtIndex}
                setupValues={setupValues}
                singleValueDescriptorValues={singleValueDescriptorValues}/>
    );

    return (
        <div className={styles.descriptorValueList}>
            {renderedValues}
        </div>
    );
};

class DescriptorValue extends Component {
    componentDidUpdate(prevProps) {
        if (prevProps.editMode !== this.props.editMode) {
            if (this.props.editMode) {
                this.focusEditor();
            } else {
                this.focusNextElement();
            }
        }
    }

    render() {
        const {
            value,
            editorValue,
            dataType,
            index,
            highlighted,
            removeValue,
            editMode,
            setupValues,
            singleValueDescriptorValues,
            setValue,
            descriptorId,
            dataSourceId, // if there are no entries for this data source id, then
            // we display the incomplete setup message.
            dataset,
            markValueInvalid,
        } = this.props;

        const valueClassName = highlighted ? styles.descriptorValueSelected : styles.descriptorValue;
        if (editMode) {
            return (
                <div className={styles.descriptorValueSectionEditor}
                    data-cy="DescriptorEditor-render"
                    ref={element => this.container = element}
                    onBlur={this.handleBlur.bind(this)}
                    onKeyDown={this.handleKeyDown.bind(this)}
                    onKeyPress={this.handleKeyPress.bind(this)}>
                    <DescriptorEntry editorValue={editorValue}
                        markValueInvalid={markValueInvalid.bind(null, descriptorId, index)}
                        changeEditorValue={newValue => setValue(descriptorId, index, newValue)}
                        dataType={dataType}
                        dataSourceId={dataSourceId}
                        dataset={dataset}
                        setupValues={setupValues}
                        singleValueDescriptorValues={singleValueDescriptorValues}/>
                </div>
            );
        }
        return (
            <div ref={element => this.container = element}
                data-cy="DescriptorEditor-return"
                className={styles.descriptorValueSection}
                onClick={this.handleClick.bind(this)}>
                <DataType value={value}
                    dataType={dataType}
                    className={valueClassName} />
                <span className={styles.descriptorRemove}>
                    <span onClick={e => removeValue(descriptorId, index)}
                        data-cy="DescriptorEditor-removeValue"
                        className={icons.materialIcons}>clear</span>
                </span>
            </div>
        );
    }

    focusEditor() {
        if (!this.container) {
            return null;
        }
        const element = this.container.querySelector('input,select');
        if (!element) {
            return;
        }
        if (lodash.isFunction(element.select)) {
            element.select();
        } else if (lodash.isFunction(element.focus)) {
            element.focus();
        }
    }

    focusNextElement() {
        if (!this.container) {
            return null;
        }
        const descriptorEditor = this.container.parentNode.parentNode;
        const element = descriptorEditor.querySelector('input,select');
        if (!element) {
            return;
        }
        if (lodash.isFunction(element.select)) {
            element.select();
        } else if (lodash.isFunction(element.focus)) {
            element.focus();
        }
    }

    handleClick(e) {
        const LEFT_BUTTON = 0;
        if (e.button !== LEFT_BUTTON) {
            return;
        }
        e.stopPropagation();
        e.preventDefault();
        const { editValueAtIndex, index } = this.props;
        editValueAtIndex(index);
    }

    handleBlur(e) {
        const { descriptorId, index, setValueEditMode } = this.props;
        setValueEditMode(descriptorId, index, false);
    }

    handleKeyPress(e) {
        const { descriptorId, index, editorValue, commitValueChange } = this.props;
        switch(e.key) {
            case 'Enter':
                if (e.shiftKey) {
                    return;
                }
                e.stopPropagation();
                commitValueChange(descriptorId, index, editorValue);
                break;
        }
    }

    handleKeyDown(e) {
        const { descriptorId, index, editorValue, commitValueChange } = this.props;
        switch(e.key) {
            case 'Tab':
                commitValueChange(descriptorId, index, editorValue);
                break;
            case 'ArrowUp':
                e.stopPropagation();
                break;
            case 'ArrowDown':
                e.stopPropagation();
                break;
        }
    }
}


export default connect((state) => {
    // get the dataSourceItems object from the state
    const dataSourceItems = lodash.get(state, 'dataSourceItems', {});
    // get the allowed values from the state
    const allowedDataSourceValues = lodash.get(state, 'allowedDataSourceValues', {});

    return {
        dataSourceItems,
        allowedDataSourceValues,
    };
}, {
    changeEditorValue,
    addValue,
    removeValue,
    markValueInvalid,
    setHighlightIndex,
    setMessage,
    setValueEditMode,
    setValue,
    commitValueChange,
    markChangeValueInvalid,
})(DescriptorEditor);
